/**
 * ModelManagementPage
 *
 * Replaces the legacy ModelsConfigPage at /settings/models.
 * Shows all platform providers with their models in a tabular view.
 * Includes availability badges (Global/Restricted) and management actions.
 *
 * Path: /settings/models
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Globe,
  Lock,
  Loader2,
  Server,
  ChevronDown,
  ChevronRight,
  Settings,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { usePlatformProviders, useProviderModels } from '../hooks/usePlatformProviders';
import { EditModelAvailabilityModal } from '../components/EditModelAvailabilityModal';
import { getModelStatusStyle } from '../lib/statusStyles';
import type { PlatformProvider, PlatformModel } from '../types';
import { PROVIDER_METADATA } from '../types';
import { ProviderIcon } from '../components/ProviderIcons';
import { ModelPriceCell } from '@/features/model-intelligence';

// =============================================================================
// Provider Row (Expandable)
// =============================================================================

interface ProviderRowProps {
  provider: PlatformProvider;
}

function ProviderRow({ provider }: ProviderRowProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [editingModel, setEditingModel] = useState<PlatformModel | null>(null);

  const { data: models, isLoading: isLoadingModels } = useProviderModels(
    expanded ? provider.id : ''
  );

  const metadata = PROVIDER_METADATA[provider.provider_type] || {
    displayName: provider.provider_type,
    iconBg: 'bg-gray-100 dark:bg-gray-700',
  };

  return (
    <>
      {/* Provider header row */}
      <div
        className={cn(
          'flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
          'border-b dark:border-gray-700',
          expanded && 'bg-gray-50 dark:bg-gray-800/30'
        )}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Expand icon */}
        <div className="flex-shrink-0 w-5">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>

        {/* Provider icon */}
        <div
          className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0',
            metadata.iconBg
          )}
        >
          <ProviderIcon
            providerType={provider.provider_type}
            size={18}
            className="w-[18px] h-[18px] object-contain"
          />
        </div>

        {/* Provider info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {provider.display_name}
            </span>
            <Badge
              variant={provider.is_active ? 'default' : 'secondary'}
              className={cn(
                'text-[10px] px-1.5 py-0',
                provider.is_active
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800'
                  : ''
              )}
            >
              {provider.is_active ? t('admin.llmConfig.modelManagement.active') : t('admin.llmConfig.modelManagement.inactive')}
            </Badge>
          </div>
        </div>

        {/* Provider status */}
        <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
          <Layers className="w-4 h-4" />
          {provider.status}
        </div>

        {/* Actions */}
        <Link
          to="/settings/platform-providers"
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0"
        >
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      {/* Expanded models */}
      {expanded && (
        <div className="border-b dark:border-gray-700">
          {isLoadingModels ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : models && models.length > 0 ? (
            <div className="bg-gray-50/50 dark:bg-gray-800/20">
              {/* Model table header */}
              <div className="grid grid-cols-[1fr,100px,100px,100px,100px,150px,80px] gap-2 px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700/50 pl-16">
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.model')}</div>
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.category')}</div>
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.availability')}</div>
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.context')}</div>
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.status')}</div>
                <div>{t('admin.llmConfig.modelManagement.tableHeaders.price')}</div>
                <div></div>
              </div>

              {/* Model rows */}
              {models.map((model) => (
                <ModelRow
                  key={model.id}
                  model={model}
                  onEditAvailability={() => setEditingModel(model)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
              {t('admin.llmConfig.modelManagement.noModels')}
            </div>
          )}
        </div>
      )}

      {/* Edit availability modal */}
      {editingModel && (
        <EditModelAvailabilityModal
          model={editingModel}
          onClose={() => setEditingModel(null)}
        />
      )}
    </>
  );
}

// =============================================================================
// Model Row
// =============================================================================

interface ModelRowProps {
  model: PlatformModel;
  onEditAvailability: () => void;
}

function ModelRow({ model, onEditAvailability }: ModelRowProps) {
  const { t } = useI18n();
  const categoryLabels: Record<string, string> = {
    main: t('admin.llmConfig.modelManagement.categories.main'),
    router: t('admin.llmConfig.modelManagement.categories.router'),
    tools: t('admin.llmConfig.modelManagement.categories.tools'),
    embedding: t('admin.llmConfig.modelManagement.categories.embedding'),
  };

  const categoryColors: Record<string, string> = {
    main: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    router: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    tools: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    embedding: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  };

  const formatContextWindow = (tokens: number) => {
    if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(0)}M`;
    if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
    return tokens.toString();
  };

  return (
    <div className="grid grid-cols-[1fr,100px,100px,100px,100px,150px,80px] gap-2 px-4 py-3 items-center hover:bg-gray-100/50 dark:hover:bg-gray-700/20 transition-colors border-b dark:border-gray-700/30 last:border-0 pl-16">
      {/* Model name */}
      <div className="min-w-0">
        <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
          {model.display_name}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">
          {model.model_id}
        </div>
      </div>

      {/* Category */}
      <div>
        <span
          className={cn(
            'px-2 py-0.5 text-[10px] font-medium rounded-full',
            categoryColors[model.category] || 'bg-gray-100 text-gray-600'
          )}
        >
          {categoryLabels[model.category] || model.category}
        </span>
      </div>

      {/* Availability */}
      <div>
        <button
          onClick={onEditAvailability}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full cursor-pointer transition-opacity hover:opacity-80',
            model.availability === 'global'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          )}
        >
          {model.availability === 'global' ? (
            <Globe className="w-3 h-3" />
          ) : (
            <Lock className="w-3 h-3" />
          )}
          {model.availability === 'global' ? t('admin.llmConfig.modelManagement.global') : t('admin.llmConfig.modelManagement.restricted')}
        </button>
      </div>

      {/* Context window */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {formatContextWindow(model.context_window)}
      </div>

      {/* Status (model_status from health check) */}
      <div>
        {(() => {
          const statusInfo = getModelStatusStyle(model.model_status);
          const StatusIcon = statusInfo.icon;
          return (
            <div className="flex items-center gap-1">
              <StatusIcon
                className={cn('w-4 h-4 flex-shrink-0', statusInfo.className)}
                aria-label={`Status: ${model.model_status}`}
              />
              <span className="text-[10px] text-gray-600 dark:text-gray-400 capitalize">
                {model.model_status}
              </span>
            </div>
          );
        })()}
      </div>

      {/* Price (Sprint 285 G: aditivo desde model-intel; «sin dato» sin permiso) */}
      <div className="min-w-0" data-testid={`catalog-price-${model.model_id}`}>
        <ModelPriceCell modelRef={model.model_id} compact />
      </div>

      {/* Edit action */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={onEditAvailability}
          className="h-7 px-2 text-xs"
        >
          {t('admin.llmConfig.modelManagement.edit')}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// ModelManagementPage
// =============================================================================

export function ModelManagementPage() {
  const { t } = useI18n();
  const { data: providers, isLoading } = usePlatformProviders(true);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('admin.llmConfig.modelManagement.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('admin.llmConfig.modelManagement.description')}
            </p>
          </div>
          <Link to="/settings/platform-providers">
            <Button variant="outline">
              <Server className="w-4 h-4 mr-2" />
              {t('admin.llmConfig.modelManagement.manageProviders')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Availability legend */}
      <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span>{t('admin.llmConfig.modelManagement.global')} — {t('admin.llmConfig.modelManagement.globalDescription')}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span>{t('admin.llmConfig.modelManagement.restricted')} — {t('admin.llmConfig.modelManagement.restrictedDescription')}</span>
        </span>
      </div>

      {/* Providers + Models */}
      {providers && providers.length > 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border dark:border-gray-700 overflow-hidden">
          {providers.map((provider) => (
            <ProviderRow key={provider.id} provider={provider} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 px-4 border rounded-lg bg-muted/20">
          <Server className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('admin.llmConfig.modelManagement.noProviders')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('admin.llmConfig.modelManagement.noProvidersHint')}
          </p>
          <Link to="/settings/platform-providers">
            <Button>
              <Server className="w-4 h-4 mr-2" />
              {t('admin.llmConfig.modelManagement.configureProviders')}
            </Button>
          </Link>
        </div>
      )}

      {/* Info section */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <Server className="w-5 h-5" />
          {t('admin.llmConfig.modelManagement.availabilityTitle')}
        </h3>
        <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
          {t('admin.llmConfig.modelManagement.availabilityDescription')}
        </p>
      </div>
    </div>
  );
}

export default ModelManagementPage;
